/**
 * Onboarding playbook — sales to delivery handoff automation.
 *
 * When a proposal is accepted or contract is signed, spinUpOnboarding
 * creates a sequence of ordered tasks that guide the engagement through
 * discovery, setup, and delivery. Each task is a visible, deletable,
 * reassignable to-do on the timeline — reversible and auditable,
 * never a hidden state machine.
 */

import { createTask, listTasks } from '../db/tasks';
import type { BusinessDoc } from '../db/types';
import { logPersonActivity } from '../db/people';

/**
 * One step in the onboarding playbook.
 * `offsetDays` is the due date relative to the document's close date
 * (a proposal's acceptance or a contract's signature).
 */
export interface PlaybookStep {
  title: string;
  offsetDays: number;
  note: string;
}

/**
 * Default onboarding playbook for accepted proposals / signed contracts.
 * Six ordered steps covering the full sales → delivery → review arc.
 */
export const ONBOARDING_PLAYBOOK: PlaybookStep[] = [
  {
    title: 'Kickoff call scheduled',
    offsetDays: 2,
    note: 'Hold a kickoff meeting with the client. Align on success criteria, timeline, and first sprint scope.',
  },
  {
    title: 'Success criteria & AQI baseline agreed',
    offsetDays: 5,
    note: 'Document what success looks like. Establish the baseline AQI score and the target score for this engagement.',
  },
  {
    title: 'Systems access & integrations',
    offsetDays: 7,
    note: 'Provision access to the client\'s systems. Connect CRM, comms, knowledge base, and other data sources.',
  },
  {
    title: 'First agents configured',
    offsetDays: 10,
    note: 'Deploy and test the first agents (ticket, research, outreach). Run them in simulation mode first, then live.',
  },
  {
    title: 'First deliverable shipped',
    offsetDays: 21,
    note: 'Land the first measurable outcome. This is the proof-of-concept moment that builds confidence.',
  },
  {
    title: '30-day review booked',
    offsetDays: 30,
    note: 'Schedule the first sprint review. Review progress against AQI, surface learnings, refine scope.',
  },
];

/**
 * Plain ISO date (YYYY-MM-DD) by adding days to a baseline.
 */
function addDaysToIso(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/**
 * Spin up an onboarding playbook for an accepted/signed proposal or contract.
 *
 * Creates one ProjectTask per step with:
 * - Title prefixed with the client name
 * - Due date = doc acceptance date + step offsetDays
 * - Assignee = doc.ownerName (or null if not provided)
 * - Priority: 'high' for the first two steps, 'medium' for the rest
 *
 * Idempotent: checks for existing tasks with the same prefix + step title
 * and skips creation if found. If the doc is for a person (prospect), also
 * logs a system activity entry.
 *
 * Returns the number of tasks created.
 */
export async function spinUpOnboarding(doc: BusinessDoc): Promise<number> {
  // Anchor due dates to when the deal actually closed (`acceptedAt`, stamped
  // by setDocumentStatus on the won transition), falling back to createdAt
  // only for legacy/seed docs that predate the field. Anchoring to createdAt
  // for a proposal drafted weeks before it was accepted would land every
  // onboarding task already overdue on day one.
  const acceptanceDate = (doc.acceptedAt ?? doc.createdAt).split('T')[0]; // YYYY-MM-DD

  const existingTasks = await listTasks();

  let tasksCreated = 0;

  for (let i = 0; i < ONBOARDING_PLAYBOOK.length; i++) {
    const step = ONBOARDING_PLAYBOOK[i];
    const dueDate = addDaysToIso(acceptanceDate, step.offsetDays);

    // Idempotence: check if a task with this prefix + step title already exists
    const taskTitle = `${doc.entityName} · ${step.title}`;
    const alreadyExists = existingTasks.some((t) => t.title === taskTitle);

    if (alreadyExists) {
      continue;
    }

    const priority = i < 2 ? 'high' : 'medium';

    await createTask({
      title: taskTitle,
      notes: step.note,
      priority,
      assignee: doc.ownerName ?? null,
      dueDate,
      accountId: doc.entityType === 'account' ? doc.entityId : undefined,
      accountName: doc.entityType === 'account' ? doc.entityName : undefined,
      personId: doc.entityType === 'person' ? doc.entityId : undefined,
      personName: doc.entityType === 'person' ? doc.entityName : undefined,
      createdBy: 'system',
    });

    tasksCreated++;
  }

  // Log a system activity entry if the doc is attached to a person (prospect).
  // Label honestly by kind — a signed contract is not an "accepted proposal".
  if (doc.entityType === 'person' && tasksCreated > 0) {
    const verb = doc.kind === 'contract' ? 'Contract signed' : 'Proposal accepted';
    await logPersonActivity(
      doc.entityId,
      'system',
      `${verb} — onboarding playbook spun up (${tasksCreated} tasks)`,
      'argo',
    );
  }

  return tasksCreated;
}
